import React, {Component} from 'react';
import PropTypes from 'prop-types';
import compose from 'recompose/compose';
import withProps from 'recompose/withProps';
import {connect} from 'react-redux';
import {Field, reduxForm, submit} from 'redux-form';
import translate from 'admin-on-rest/lib/i18n/translate';
import {CardText} from 'material-ui/Card';
import IconButton from 'material-ui/IconButton';
import ActionHide from 'material-ui/svg-icons/action/highlight-off';
import muiThemeable from 'material-ui/styles/muiThemeable';
import autoprefixer from 'material-ui/utils/autoprefixer';
import RaisedButton from 'material-ui/RaisedButton';

const getStyles = ({palette: {primary1Color}}) => ({
    card: {
        marginTop: '-14px',
        paddingTop: 0,
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        flexWrap: 'wrap'
    },
    body: {display: 'flex', alignItems: 'flex-end'},
    buttonContainer: {display: 'block', width: '100%', margin: '1em 0 0 0'},
    spacer: {width: 48},
    icon: {color: primary1Color || '#00bcd4', paddingBottom: 0},
    clearFix: {clear: 'right'}
});

const emptyRecord = {};

/**
 * Handle Redux form change
 *
 * @param values
 * @param dispatch
 * @param props
 */
const handleFilterFormChange = (values, dispatch, props) => {
    if (props.updateOnChange) {
        props.setFilters(values);
    }
};

/**
 * Handle Redux form submission
 *
 * @param values
 * @param dispatch
 * @param props
 */
const handleFilterFormSubmit = (values, dispatch, props) => {
    props.setFilters(values);
};

export class FilterFormWithSubmit extends Component {

    /**
     * Determine display for Dependent Filters
     *
     * @param values
     * @param filterElement
     * @return {boolean}
     */
    displayDependentFilter(values, filterElement) {
        const dependsOn = filterElement.props.dependsOn;
        const dependsOnFilterValue = filterElement.props.dependsOnFilterValue;
        let priorFilter;
        if (dependsOn) {
            if (!dependsOnFilterValue) {
                // If the prior Filter's value equals the Dependent Filter's source:
                priorFilter = values[dependsOn];
                if (priorFilter === filterElement.props.source) {
                    return true;
                }
            } else if (dependsOnFilterValue && values[dependsOn]) {
                // If the Dependent Filter will retrieve its data based on the prior Filter's value:
                return true;
            }
        }
        return false;
    }

    getShownFilters() {
        const {filters, displayedFilters, initialValues} = this.props;
        return filters.filter(
            filterElement =>
                this.displayDependentFilter(initialValues, filterElement) ||
                filterElement.props.alwaysOn ||
                displayedFilters[filterElement.props.source] ||
                (!filterElement.props.dependsOn && typeof initialValues[filterElement.props.source] !== 'undefined')
        );
    }

    handleHide = event =>
        this.props.hideFilter(event.currentTarget.dataset.key);

    /**
     * Trigger submit on form
     */
    handleFilterSubmit = (e) => {
        e.preventDefault();
        this.props.dispatch(submit('filterForm'));
    };

    render() {
        const {
            displayButton,
            filterButton,
            resource,
            translate,
            muiTheme
        } = this.props;

        // Use a fake prefixer when none is returned. Useful for tests
        const prefix = autoprefixer(muiTheme) || (style => style);
        const styles = getStyles(muiTheme);

        return (
            <div>
                <CardText style={prefix(styles.card)}>
                    {this.getShownFilters()
                        .reverse()
                        .map(filterElement => (
                            <div
                                key={filterElement.props.source}
                                data-source={filterElement.props.source}
                                className="filter-field"
                                style={
                                    filterElement.props.style ||
                                    prefix(styles.body)
                                }
                            >
                                {(filterElement.props.alwaysOn || filterElement.props.dependsOn) ? (
                                    <div style={prefix(styles.spacer)}>
                                        &nbsp;
                                    </div>
                                ) : (
                                    <IconButton
                                        iconStyle={prefix(styles.icon)}
                                        className="hide-filter"
                                        onClick={this.handleHide}
                                        data-key={filterElement.props.source}
                                        tooltip={translate(
                                            'aor.action.remove_filter'
                                        )}
                                    >
                                        <ActionHide/>
                                    </IconButton>
                                )}
                                <div>
                                    <Field
                                        allowEmpty
                                        {...filterElement.props}
                                        name={filterElement.props.source}
                                        component={filterElement.type}
                                        resource={resource}
                                        record={emptyRecord}
                                    />
                                </div>
                            </div>
                        ))}
                    {displayButton &&
                    <span style={styles.buttonContainer}>
                        <RaisedButton
                            type="submit"
                            primary
                            onClick={(e) => this.handleFilterSubmit(e)}
                            //disabled={submitting}
                            label={filterButton}
                        />
                        </span>
                    }
                </CardText>
                <div style={prefix(styles.clearFix)}/>
            </div>
        );
    }
}

FilterFormWithSubmit.propTypes = {
    resource: PropTypes.string.isRequired,
    filters: PropTypes.arrayOf(PropTypes.node).isRequired,
    dependsOnFilterValue: PropTypes.bool,
    displayButton: PropTypes.bool,
    displayedFilters: PropTypes.object.isRequired,
    filterButton: PropTypes.string,
    hideFilter: PropTypes.func.isRequired,
    initialValues: PropTypes.object,
    translate: PropTypes.func.isRequired,
    updateOnChange: PropTypes.bool,
    muiTheme: PropTypes.object.isRequired
};

FilterFormWithSubmit.defaultProps = {
    filterButton: 'Search',
    dependsOnFilterValue: false,
    displayButton: true,
    updateOnChange: false
};

export const mergeInitialValuesWithDefaultValues = ({
                                                        initialValues,
                                                        filters
                                                    }) => ({
    initialValues: {
        ...filters
            .filter(
                filterElement =>
                    filterElement.props.alwaysOn &&
                    filterElement.props.defaultValue
            )
            .reduce(
                (acc, filterElement) => ({
                    ...acc,
                    [filterElement.props.source]:
                    filterElement.props.defaultValue
                }),
                {}
            ),
        ...initialValues
    }
});

const enhance = compose(
    muiThemeable(),
    translate,
    withProps(mergeInitialValuesWithDefaultValues),
    reduxForm({
        form: 'filterForm',
        enableReinitialize: true,
        onChange: (values, dispatch, props) => handleFilterFormChange(values, dispatch, props),
        onSubmit: (values, dispatch, props) => handleFilterFormSubmit(values, dispatch, props)
    }),
    connect()
);

export default enhance(FilterFormWithSubmit);



// WEBPACK FOOTER //
// ./src/Fuse/admin/FilterFormWithSubmit.js